import { colors } from '@apollo/brand';
import { gql, useQuery } from '@apollo/client';
import { Button, Link, Spinner, customColors } from '@apollo/orbit';
import { Telescope } from '@apollo/space-kit/illustrations/Telescope';
import classnames from 'classnames';
import _ from 'lodash';
import React, { ReactNode, useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router';

import { useInternalGraphLinking } from 'src/app/graph/hooks/useInternalGraphLinking';
import {
  insightsRouteConfig,
  variantSettingsRouteConfig,
} from 'src/app/graph/routes';
import { StatsTimeRangeResult } from 'src/components/affectedOperations/affectedOperationsHelpers';
import { CursorProvider } from 'src/components/charts/helpers/Cursor';
import { LineChart } from 'src/components/charts/LineChart';
import {
  ClientName,
  ClientVersionList,
} from 'src/components/clientVersionList/ClientVersionList';
import { ContactSupportLink } from 'src/components/ContactSupportCTAs';
import { MomentTimestamp } from 'src/components/timestamp/Timestamp';
import Config from 'src/lib/config';
import { date, findReasonableResolution } from 'src/lib/date/date';
import { format } from 'src/lib/format';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { semverSort } from 'src/lib/semver';

import {
  ChangeList,
  ChangeListChangeOnOperationFragment,
} from '../changeList/ChangeList';
import { OperationBodyModal } from '../operationBodyModal/OperationBodyModal';

import {
  LegacyOverrideButton,
  overrideButtonAffectedQueryFragment,
} from './overrideButton/LegacyOverrideButton';

// TODO: rename this and move to new CheckOperationDetails
export const CheckOperationDetailsAffectedQueryFragment = gql`
  fragment CheckOperationDetailsAffectedQueryFragment on AffectedQuery {
    id
    name
    signature
    isValid
    markedAsSafe
    markedAsIgnored
    changes {
      ...ChangeListChangeOnOperationFragment
    }
    ...OverrideButtonAffectedQueryFragment
  }
  ${overrideButtonAffectedQueryFragment}
  ${ChangeListChangeOnOperationFragment}
`;

export const checkOperationsDetailsQuery = gql`
  query CheckOperationsDetailsQuery(
    $graphId: ID!
    $timeFrom: Timestamp!
    $timeTo: Timestamp!
    $resolution: Resolution!
    $filter: ServiceQueryStatsFilter!
  ) {
    service(id: $graphId) {
      id
      title
      usageStats: statsWindow(
        from: $timeFrom
        to: $timeTo
        resolution: $resolution
      ) {
        queryStats(filter: $filter) {
          timestamp
          metrics {
            totalRequestCount
          }
        }
      }
      perClientStats: statsWindow(from: $timeFrom, to: $timeTo) {
        queryStats(filter: $filter) {
          groupBy {
            clientName
            clientVersion
          }
          metrics {
            totalRequestCount
          }
        }
      }
      perVariantStats: statsWindow(from: $timeFrom, to: $timeTo) {
        queryStats(filter: $filter) {
          groupBy {
            schemaTag
          }
          metrics {
            totalRequestCount
          }
        }
      }
    }
  }
`;

interface Props {
  graphId: string;
  graphVariant: string;
  taskId: string;
  operation: GraphQLTypes.CheckOperationDetailsAffectedQueryFragment;
  statsWindow: StatsTimeRangeResult;
  canViewClientVersions: boolean;
}

function groupClientsByNameToVersions(
  queryStats: GraphQLTypes.CheckOperationsDetailsQuery_service_perClientStats_queryStats[],
): Record<string, string[]> {
  const clientsGroupedByNameDictionary = _.groupBy(
    queryStats,
    (stat) => stat.groupBy.clientName ?? '',
  );
  const clientVersionsGroupedByName = _.mapValues(
    clientsGroupedByNameDictionary,
    (queryStatsForClient) =>
      semverSort(
        _.uniq(
          queryStatsForClient.map((stat) => stat.groupBy.clientVersion ?? ''),
        ),
      ),
  );
  return clientVersionsGroupedByName;
}

const Widget = ({
  children,
  title,
  loading,
}: {
  children: React.ReactNode;
  title?: ReactNode;
  loading?: boolean;
}) => (
  <div className="pb-8">
    {title && (
      <div className="border-b border-primary pb-2">
        <div className="flex items-center justify-between text-xs font-semibold uppercase text-neutral">
          {title}
        </div>
      </div>
    )}
    {loading ? (
      <div className="flex h-32 w-full items-center justify-center">
        <Spinner size="sm" />
      </div>
    ) : (
      children
    )}
  </div>
);

export const LegacyCheckOperationDetails = ({
  graphId,
  graphVariant,
  taskId,
  operation,
  statsWindow,
  canViewClientVersions,
}: Props) => {
  const location = useLocation();
  const [operationBodyModalVisible, setOperationBodyModalVisible] =
    useState(false);
  const onOperationBodyModalClose = useCallback(() => {
    setOperationBodyModalVisible(false);
  }, [setOperationBodyModalVisible]);

  const timeFrom = statsWindow.queryableTimeRange?.timeFrom ?? '-86400';
  const timeTo = statsWindow.queryableTimeRange?.timeTo ?? '-0';
  if (!taskId) {
    throw new Error('expected to be loaded on a route with checkId');
  }

  const diffSeconds = date.getMinDifference(timeFrom, timeTo) * 60;
  const resolution = findReasonableResolution(diffSeconds);
  const { data, loading, error } = useQuery<
    GraphQLTypes.CheckOperationsDetailsQuery,
    GraphQLTypes.CheckOperationsDetailsQueryVariables
  >(checkOperationsDetailsQuery, {
    variables: {
      graphId,
      timeFrom,
      timeTo,
      resolution,
      filter: {
        queryId: operation.id,
      },
    },
  });

  const clientVersionsGroupedByName = Object.entries(
    groupClientsByNameToVersions(
      data?.service?.perClientStats?.queryStats ?? [],
    ),
  );

  const chartData = useMemo(
    () => [
      {
        colorGradientTop: `${colors.primitives.blue[300]}3C`,
        colorGradientBottom: `${colors.primitives.blue[300]}00`,
        primaryColor: customColors.flowchart.d,
        cursorColor: customColors.brand.accent,
        unit: ' RPM',
        data: data?.service?.usageStats?.queryStats ?? [],
        getValue: (
          stats: GraphQLTypes.CheckOperationsDetailsQuery_service_usageStats_queryStats,
        ) => ({
          timestamp: stats.timestamp,
          value:
            stats.metrics.totalRequestCount /
            date.getMinDifference(
              stats.timestamp,
              date.incrementByResolution(stats.timestamp, resolution),
            ),
        }),
      },
    ],
    [data?.service?.usageStats?.queryStats, resolution],
  );

  const { currentVariantRouteConfig } = useInternalGraphLinking();

  return (
    <>
      <h1
        className={classnames(
          'mb-2 text-xs font-semibold uppercase',
          operation.markedAsIgnored || operation.markedAsSafe
            ? 'text-icon-primary'
            : operation.isValid
              ? 'text-icon-warning'
              : 'text-icon-error',
        )}
      >
        {operation.isValid ? 'Changed' : 'Broken'} Operation{' '}
        {operation.markedAsIgnored
          ? '(Ignored)'
          : operation.markedAsSafe
            ? '(Safe)'
            : ''}
      </h1>
      <h1 className="pb-5 text-lg">
        <span className="font-mono text-lg text-secondary">
          {format.shortQueryId(operation.id)}
        </span>{' '}
        {operation.name}
      </h1>
      {error || data?.service?.usageStats?.queryStats.length === 0 ? (
        <div className="flex h-32 w-full flex-col items-center justify-center">
          <Telescope />
          <div className="pt-3 text-base text-secondary">
            {!statsWindow.queryableTimeRange && statsWindow.errorMessage ? (
              <span>
                No usage for operation in the past{' '}
                {statsWindow.maxRangeInDaysForChecks} day(s).{' '}
                <ContactSupportLink>
                  Contact usage to request a longer retention window.
                </ContactSupportLink>
              </span>
            ) : (
              <span>
                Missing usage data for check.{' '}
                <ContactSupportLink>
                  Contact us if this problem persists.
                </ContactSupportLink>
              </span>
            )}
          </div>
        </div>
      ) : (
        <>
          <Widget>
            <div className="flex flex-wrap items-center">
              <Button
                type="button"
                variant="secondary"
                className="my-1 mr-2 px-3 py-2"
                onClick={() => setOperationBodyModalVisible(true)}
              >
                <span className="truncate">View operation body</span>
              </Button>
              <LegacyOverrideButton
                graphId={graphId}
                checkId={taskId}
                operation={operation}
              />
            </div>

            {operationBodyModalVisible && (
              <OperationBodyModal
                isOpen={operationBodyModalVisible}
                graphId={graphId}
                graphVariant={graphVariant}
                operationId={operation.id}
                onClose={onOperationBodyModalClose}
              />
            )}
            {operationBodyModalVisible && (
              <OperationBodyModal
                isOpen={operationBodyModalVisible}
                graphId={graphId}
                graphVariant={graphVariant}
                operationId={operation.id}
                onClose={onOperationBodyModalClose}
              />
            )}
          </Widget>
          <Widget title="Impacting Changes">
            <div className="pt-2 text-base">
              {operation.changes && operation.changes.length > 0 ? (
                <div className="overflow-x-auto">
                  <ChangeList changes={operation.changes} />
                </div>
              ) : (
                <span className="text-secondary">
                  No impacting changes found. Check statuses are maintained
                  forever, but details may be lost after 30 days.
                </span>
              )}
            </div>
          </Widget>
          <Widget
            title={
              <span>
                Request Rate (RPM) From <MomentTimestamp value={timeFrom} /> -{' '}
                <MomentTimestamp value={timeTo} />
              </span>
            }
            loading={loading}
          >
            <div className="pt-2">
              <CursorProvider>
                <LineChart
                  stacked
                  stacks={chartData}
                  className="h-48 pt-4"
                  resolution={resolution}
                />
              </CursorProvider>
            </div>
          </Widget>
          <Widget
            title={
              <>
                <span>
                  Used By Client
                  {clientVersionsGroupedByName.length !== 1 ? 's' : ''}
                </span>
                <Link
                  to={variantSettingsRouteConfig.locationFrom({
                    location,
                    patch: {
                      tab: 'checks_operations',
                      graphVariant,
                    },
                    fromRouteConfig: currentVariantRouteConfig,
                  })}
                >
                  Edit in Configuration
                </Link>
              </>
            }
            loading={loading}
          >
            {clientVersionsGroupedByName.map(([name, versions]) => (
              <div key={name} className="flex pt-3">
                <ClientName className="pr-3 text-base">{name}</ClientName>
                {canViewClientVersions && (
                  <ClientVersionList
                    className="truncate text-base text-secondary"
                    clientName={name}
                    versions={versions}
                  />
                )}
              </div>
            ))}
          </Widget>
          <Widget
            title={
              <>
                <span>
                  Used By Variant
                  {(data?.service?.perVariantStats?.queryStats ?? []).length !==
                  1
                    ? 's'
                    : ''}
                </span>
                <Link
                  to={variantSettingsRouteConfig.locationFrom({
                    location,
                    patch: { tab: 'checks_operations' },
                    fromRouteConfig: currentVariantRouteConfig,
                  })}
                >
                  Edit in Configuration
                </Link>
              </>
            }
            loading={loading}
          >
            {(data?.service?.perVariantStats?.queryStats ?? []).map((stat) => (
              <div key={stat.groupBy.schemaTag ?? ''} className="flex pt-3">
                <Link
                  to={insightsRouteConfig.path({
                    graphId,
                    query: operation.id,
                    queryName: operation.name || undefined,
                    graphVariant:
                      stat.groupBy.schemaTag || Config.service.defaultVariant,
                    range: 'custom',
                    from: timeFrom,
                    to: timeTo,
                  })}
                >
                  {data?.service?.title} /{' '}
                  {stat.groupBy.schemaTag || Config.service.defaultVariant}
                </Link>
              </div>
            ))}
          </Widget>
        </>
      )}
    </>
  );
};
